import React from 'react'

import { Box, LinearProgress, Typography } from '@mui/material'

export const NEAR_CAPACITY = 80
export const AT_CAPACITY = 100

type QuotaUsageProps = {
  extra?: number
  quota?: number
  usage?: number
}

const isUnlimited = (quota: number) => quota === Infinity

// Infinity > 0 is true, so the old ratio quietly produced 0% for an unlimited quota and painted an
// empty bar as though nothing had been used.
export const quotaSeverity = (quota: number, usage: number) => {
  if (isUnlimited(quota) || quota <= 0) return undefined

  const percentage = (usage / quota) * 100

  if (percentage >= AT_CAPACITY) return 'error' as const
  if (percentage >= NEAR_CAPACITY) return 'warning' as const

  return undefined
}

export const quotaProgress = (quota: number, usage: number) => {
  if (isUnlimited(quota) || quota <= 0) return 0

  const ratio = usage / quota

  return Number.isFinite(ratio) ? Math.min(100, Math.max(0, Math.round(ratio * 100))) : 0
}

const QuotaUsage = ({ quota = 0, usage = 0, extra = 0 }: QuotaUsageProps) => {
  const severity = quotaSeverity(quota, usage)
  const progress = quotaProgress(quota, usage)

  const label = isUnlimited(quota)
    ? `${usage.toLocaleString()} of unlimited`
    : quota > 0
      ? `${usage.toLocaleString()} / ${quota.toLocaleString()}`
      : `${usage.toLocaleString()} used, no allowance`

  return (
    <Box sx={{ minWidth: '5rem' }}>
      <LinearProgress
        color={severity ?? 'primary'}
        sx={{ mb: '5px', opacity: isUnlimited(quota) ? 0.3 : 1 }}
        value={progress}
        variant="determinate"
      />
      <Typography color={severity ? `${severity}.main` : 'text.secondary'} variant="caption">
        {label}
        {extra > 0 && ` (incl. ${extra.toLocaleString()} temporary)`}
      </Typography>
    </Box>
  )
}

export default QuotaUsage
